import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Tag, Tooltip, Empty } from "antd";
import {
    CloseOutlined,
    EditOutlined,
    UserOutlined,
    LeftOutlined,
    RightOutlined,
    IdcardOutlined,
    CalendarOutlined,
    TeamOutlined,
    PhoneOutlined,
    MailOutlined,
    HomeOutlined,
    ManOutlined,
    WomanOutlined,
    BookOutlined,
    ContactsOutlined,
    FileTextOutlined,
    SafetyCertificateOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    HeartOutlined,
    EyeOutlined,
} from "@ant-design/icons";

import api from "../../../api/api";

/* ================================================================
   HELPERS
================================================================ */

const firstValue = (...values) => {
    for (const value of values) {
        if (
            value !== undefined &&
            value !== null &&
            String(value).trim() !== ""
        ) {
            return value;
        }
    }

    return null;
};

const displayValue = (value) => {
    if (value === undefined || value === null || value === "") {
        return "Not provided";
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "Not provided";
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.full_name ||
            value.title ||
            value.label ||
            value.email ||
            value.phone ||
            "Not provided"
        );
    }

    return String(value);
};

const formatDate = (value) => {
    if (!value) return "Not provided";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return displayValue(value);
    }

    return date.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const humanizeKey = (key) =>
    String(key || "")
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getStudentName = (student) =>
    firstValue(
        student?.full_name,
        student?.student_name,
        student?.name,
        [
            student?.first_name,
            student?.middle_name,
            student?.last_name,
        ]
            .filter(Boolean)
            .join(" "),
        student?.user?.full_name,
        student?.user?.username,
        "Unnamed Student"
    );

const getStudentImageValue = (student) => {
    const candidates = [
        student?.student_admission_image,
        student?.student_image,
        student?.profile_image,
        student?.profile_picture,
        student?.photo,
        student?.image,
        student?.user?.profile_image,
        student?.user?.profile_picture,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim();
        }

        if (candidate && typeof candidate === "object") {
            const url = candidate.url || candidate.image;

            if (typeof url === "string" && url.trim()) {
                return url.trim();
            }
        }
    }

    return "";
};

const getStudentImageUrl = (student) => {
    const imagePath = getStudentImageValue(student);

    if (!imagePath) return "";

    if (
        imagePath.startsWith("data:") ||
        imagePath.startsWith("blob:") ||
        /^https?:\/\//i.test(imagePath)
    ) {
        return imagePath;
    }

    try {
        const configuredBase =
            api.defaults?.baseURL ||
            process.env.REACT_APP_API_URL ||
            process.env.REACT_APP_API_BASE_URL ||
            window.location.origin;

        const apiBase = configuredBase.replace(/\/+$/, "");
        const mediaBase = apiBase.replace(/\/api\/?$/, "");

        return new URL(imagePath, `${mediaBase}/`).href;
    } catch (error) {
        console.warn("Unable to resolve student image URL:", error);
        return "";
    }
};

const isDateKey = (key) =>
    /(^date_|_date$|_at$|^dob$|birth_date|enrollment_date|admission_date|created_at|updated_at)/i.test(
        key
    );

const isExcludedKey = (key) =>
    /password|token|secret|authorization|last_login|groups|user_permissions|student_admission_image|profile_image|profile_picture|student_image|(^id$)|_id$/i.test(
        key
    );

const isSimpleValue = (value) =>
    value === null ||
    ["string", "number", "boolean"].includes(typeof value);

/* ================================================================
   DETAIL ITEM
================================================================ */

const DetailItem = ({ icon, label, value, darkMode }) => (
    <div className={`adsp-detail-item ${darkMode ? "adsp-dark" : ""}`}>
        <div className="adsp-detail-icon">{icon}</div>

        <div className="adsp-detail-copy">
            <span className="adsp-detail-label">{label}</span>
            <span className="adsp-detail-value">
                {displayValue(value)}
            </span>
        </div>
    </div>
);

/* ================================================================
   DETAIL SECTION
================================================================ */

const DetailSection = ({
    title,
    subtitle,
    icon,
    children,
    darkMode,
}) => (
    <section
        className={`adsp-detail-section ${darkMode ? "adsp-dark" : ""}`}
    >
        <div className="adsp-section-heading">
            <div className="adsp-section-heading-icon">{icon}</div>

            <div className="adsp-section-heading-copy">
                <h3>{title}</h3>
                {subtitle && <p>{subtitle}</p>}
            </div>
        </div>

        <div className="adsp-detail-grid">{children}</div>
    </section>
);

/* ================================================================
   MAIN COMPONENT
================================================================ */

const AdmissionStudentDetailsPopUp = ({
    visible = false,
    student = null,
    students = [],
    onStudentChange = () => {},
    onClose = () => {},
    onEdit = () => {},
    darkMode = false,
    sidebarOffset = 250,
}) => {
    const [activePage, setActivePage] = useState("personal");
    const [imageFailed, setImageFailed] = useState(false);

    const studentId = student?.id;

    useEffect(() => {
        setActivePage("personal");
        setImageFailed(false);
    }, [studentId]);

    const studentName = useMemo(
        () => getStudentName(student),
        [student]
    );

    const photoUrl = useMemo(
        () => getStudentImageUrl(student),
        [student]
    );

    const status = firstValue(
        student?.status,
        student?.admission_status,
        student?.student_status,
        "Unknown"
    );

    const gradeClass = firstValue(
        student?.grade_class_name,
        student?.grade_class?.name,
        student?.grade_class?.grade_name,
        student?.class_name,
        student?.grade_name
    );

    const admissionNumber = firstValue(
        student?.admission_id,
        student?.admission_id,
        student?.admission_id,
        student?.admission_id,
        student?.admission_id,
        student?.admission_id
    );

    const academicYear = firstValue(
        student?.academic_year_name,
        student?.academic_year?.name,
        student?.academic_year,
        student?.training_year_name
    );

    const gender = firstValue(student?.gender, student?.user?.gender);

    const email = firstValue(
        student?.email,
        student?.student_email,
        student?.user?.email,
        student?.parent_email
    );

    const phone = firstValue(
        student?.phone_number,
        student?.phone,
        student?.mobile_number,
        student?.contact_number,
        student?.user?.phone_number
    );

    const address = firstValue(
        student?.address,
        student?.residential_address,
        student?.home_address,
        student?.location
    );

    const division = firstValue(
        student?.division_assigned_name,
        student?.division_name,
        student?.division?.name,
        student?.division_assigned?.name
    );

    const enrollmentDate = firstValue(
        student?.enrollment_date,
        student?.admission_date,
        student?.date_enrolled
    );

    const dateOfBirth = firstValue(
        student?.date_of_birth,
        student?.dob,
        student?.birth_date
    );

    const parentName = firstValue(
        student?.parent_name,
        student?.guardian_name,
        student?.father_name,
        student?.mother_name,
        student?.next_of_kin_name,
        student?.parent?.full_name,
        student?.guardian?.full_name
    );

    const parentPhone = firstValue(
        student?.parent_phone,
        student?.parent_phone_number,
        student?.guardian_phone,
        student?.guardian_phone_number,
        student?.father_phone,
        student?.mother_phone,
        student?.next_of_kin_phone,
        student?.parent?.phone_number,
        student?.guardian?.phone_number
    );

    const parentEmail = firstValue(
        student?.parent_email,
        student?.guardian_email,
        student?.parent?.email,
        student?.guardian?.email
    );

    const relationship = firstValue(
        student?.relationship_to_student,
        student?.parent_relationship,
        student?.guardian_relationship,
        student?.relationship
    );

    const studentIndex = useMemo(() => {
        if (!student || !Array.isArray(students)) return -1;

        return students.findIndex(
            (item) => String(item?.id) === String(student?.id)
        );
    }, [student, students]);

    const canNavigate =
        Array.isArray(students) &&
        students.length > 1 &&
        studentIndex >= 0;

    const goToStudent = (direction) => {
        if (!canNavigate) return;

        const nextIndex = studentIndex + direction;

        if (nextIndex < 0 || nextIndex >= students.length) return;

        const nextStudent = students[nextIndex];

        if (nextStudent) onStudentChange(nextStudent);
    };

    const extraDetails = useMemo(() => {
        if (!student || typeof student !== "object") return [];

        const knownKeys = new Set([
            "id",
            "full_name",
            "student_name",
            "name",
            "first_name",
            "middle_name",
            "last_name",
            "student_admission_image",
            "student_image",
            "profile_image",
            "profile_picture",
            "photo",
            "image",
            "gender",
            "email",
            "student_email",
            "phone_number",
            "phone",
            "mobile_number",
            "contact_number",
            "address",
            "residential_address",
            "home_address",
            "location",
            "grade_class_name",
            "grade_class",
            "grade_name",
            "class_name",
            "division_assigned_name",
            "division_name",
            "division",
            "division_assigned",
            "academic_year_name",
            "academic_year",
            "training_year_name",
            "status",
            "admission_status",
            "student_status",
            "admission_number",
            "student_admission_number",
            "registration_number",
            "student_id_number",
            "student_code",
            "enrollment_date",
            "admission_date",
            "date_enrolled",
            "date_of_birth",
            "dob",
            "birth_date",
            "parent_name",
            "guardian_name",
            "father_name",
            "mother_name",
            "next_of_kin_name",
            "parent",
            "guardian",
            "parent_phone",
            "parent_phone_number",
            "guardian_phone",
            "guardian_phone_number",
            "father_phone",
            "mother_phone",
            "next_of_kin_phone",
            "parent_email",
            "guardian_email",
            "relationship_to_student",
            "parent_relationship",
            "guardian_relationship",
            "relationship",
            "semester_status",
            "created_at",
            "updated_at",
            "user",
        ]);

        return Object.entries(student)
            .filter(([key, value]) => {
                if (knownKeys.has(key)) return false;
                if (isExcludedKey(key)) return false;
                if (!isSimpleValue(value)) return false;

                return (
                    value !== null &&
                    value !== "" &&
                    value !== undefined
                );
            })
            .slice(0, 24)
            .map(([key, value]) => ({
                key,
                label: humanizeKey(key),
                value: isDateKey(key)
                    ? formatDate(value)
                    : displayValue(value),
            }));
    }, [student]);

    const pages = [
        {
            key: "personal",
            label: "Personal",
            icon: <UserOutlined />,
        },
        {
            key: "admission",
            label: "Admission",
            icon: <IdcardOutlined />,
        },
        {
            key: "contact",
            label: "Contact",
            icon: <PhoneOutlined />,
        },
        {
            key: "guardian",
            label: "Parent / Guardian",
            icon: <ContactsOutlined />,
        },
        ...(extraDetails.length
            ? [
                  {
                      key: "additional",
                      label: "Additional",
                      icon: <FileTextOutlined />,
                  },
              ]
            : []),
    ];

    if (!student) return null;

    const activeIndex = pages.findIndex(
        (page) => page.key === activePage
    );

    const safeActivePage =
        activeIndex >= 0 ? activePage : "personal";

    const safePageIndex = pages.findIndex(
        (page) => page.key === safeActivePage
    );

    const goToPage = (direction) => {
        const nextIndex = safePageIndex + direction;

        if (nextIndex < 0 || nextIndex >= pages.length) return;

        setActivePage(pages[nextIndex].key);
    };

    const normalizedStatus = String(status).toLowerCase();

    const isActiveStatus = [
        "enrolled",
        "active",
        "admitted",
        "registered",
        "approved",
    ].includes(normalizedStatus);

    const genderIcon =
        String(gender || "").toLowerCase() === "female" ? (
            <WomanOutlined />
        ) : (
            <ManOutlined />
        );

    return (
        <Modal
            open={visible}
            visible={visible}
            onCancel={onClose}
            footer={null}
            closable={false}
            centered
            width={1080}
            destroyOnClose={false}
            maskClosable={false}
            keyboard
            className="adsp-modal"
            wrapClassName="adsp-modal-wrap"
            transitionName=""
            maskTransitionName=""
        >
            <div
                className={`adsp-portfolio ${
                    darkMode ? "adsp-dark" : ""
                }`}
                style={{
                    "--adsp-sidebar-offset": `${Math.max(
                        0,
                        Number(sidebarOffset) || 0
                    )}px`,
                }}
            >
                <header className="adsp-header">
                    <div className="adsp-header-decoration adsp-decoration-one" />
                    <div className="adsp-header-decoration adsp-decoration-two" />

                    <div className="adsp-header-topline">
                        <div className="adsp-brand">
                            <span className="adsp-brand-icon">
                                <IdcardOutlined />
                            </span>

                            <div>
                                <span className="adsp-brand-eyebrow">
                                    STUDENT RECORDS
                                </span>
                                <span className="adsp-brand-title">
                                    Admission Portfolio
                                </span>
                            </div>
                        </div>

                        <Tooltip title="Close portfolio">
                            <button
                                type="button"
                                className="adsp-close-button"
                                onClick={onClose}
                                aria-label="Close student portfolio"
                            >
                                <CloseOutlined />
                            </button>
                        </Tooltip>
                    </div>

                    <div className="adsp-profile">
                        <div className="adsp-profile-photo">
                            <div className="adsp-profile-fallback">
                                <UserOutlined />
                            </div>

                            {photoUrl && !imageFailed && (
                                <img
                                    src={photoUrl}
                                    alt={studentName}
                                    onError={() => setImageFailed(true)}
                                />
                            )}

                            <span className="adsp-photo-badge">
                                <SafetyCertificateOutlined />
                            </span>
                        </div>

                        <div className="adsp-profile-main">
                            <span className="adsp-profile-kicker">
                                STUDENT ADMISSION
                            </span>

                            <h2>{studentName}</h2>

                            <div className="adsp-profile-meta">
                                <span>
                                    <IdcardOutlined />
                                    {`Admission No. ${displayValue(
                                        admissionNumber
                                    )}`}
                                </span>

                                <span>
                                    <TeamOutlined />
                                    {displayValue(gradeClass)}
                                </span>
                            </div>

                            <div className="adsp-profile-tags">
                                <Tag
                                    className={
                                        isActiveStatus
                                            ? "adsp-status adsp-status-active"
                                            : "adsp-status adsp-status-other"
                                    }
                                >
                                    <span className="adsp-status-dot" />
                                    {displayValue(status)}
                                </Tag>

                                {gender && (
                                    <Tag className="adsp-gender-tag">
                                        {genderIcon}
                                        {displayValue(gender)}
                                    </Tag>
                                )}
                            </div>
                        </div>

                        <div className="adsp-profile-year">
                            <span>ACADEMIC YEAR</span>
                            <strong>{displayValue(academicYear)}</strong>
                            <span className="adsp-year-caption">
                                Official student record
                            </span>
                        </div>
                    </div>

            
                </header>

                <nav
                    className="adsp-page-tabs"
                    aria-label="Student portfolio sections"
                >
                    {pages.map((page) => (
                        <button
                            type="button"
                            key={page.key}
                            className={`adsp-page-tab ${
                                safeActivePage === page.key
                                    ? "adsp-page-tab-active"
                                    : ""
                            }`}
                            onClick={() => setActivePage(page.key)}
                            aria-current={
                                safeActivePage === page.key
                                    ? "page"
                                    : undefined
                            }
                        >
                            <span className="adsp-page-tab-icon">
                                {page.icon}
                            </span>
                            <span>{page.label}</span>
                        </button>
                    ))}
                </nav>

                <main className="adsp-body">
                    {safeActivePage === "personal" && (
                        <div className="adsp-page-content">
                            <DetailSection
                                title="Personal Information"
                                subtitle="Student identity and basic details"
                                icon={<UserOutlined />}
                                darkMode={darkMode}
                            >
                                <DetailItem icon={<UserOutlined />} label="Full Name" value={studentName} darkMode={darkMode} />
                                <DetailItem icon={genderIcon} label="Gender" value={gender} darkMode={darkMode} />
                                <DetailItem icon={<CalendarOutlined />} label="Date of Birth" value={dateOfBirth ? formatDate(dateOfBirth) : null} darkMode={darkMode} />
                                <DetailItem icon={<IdcardOutlined />} label="Admission Number" value={admissionNumber} darkMode={darkMode} />
                                <DetailItem icon={<HeartOutlined />} label="Semester Status" value={student?.semester_status} darkMode={darkMode} />
                                <DetailItem icon={<CalendarOutlined />} label="Record Created" value={student?.created_at ? formatDate(student.created_at) : null} darkMode={darkMode} />
                            </DetailSection>

                            <div className="adsp-information-note">
                                <span className="adsp-note-icon">
                                    <EyeOutlined />
                                </span>
                                <span>
                                    Review the student's identity and admission
                                    information using the sections above.
                                    Select another section to view more details.
                                </span>
                            </div>
                        </div>
                    )}

                    {safeActivePage === "admission" && (
                        <div className="adsp-page-content">
                            <DetailSection
                                title="Admission & Placement"
                                subtitle="Academic assignment and enrollment"
                                icon={<BookOutlined />}
                                darkMode={darkMode}
                            >
                                <DetailItem icon={<IdcardOutlined />} label="Admission Number" value={admissionNumber} darkMode={darkMode} />
                                <DetailItem icon={<TeamOutlined />} label="Grade / Class" value={gradeClass} darkMode={darkMode} />
                                <DetailItem icon={<BookOutlined />} label="Division / Session" value={division} darkMode={darkMode} />
                                <DetailItem icon={<CalendarOutlined />} label="Academic Year" value={academicYear} darkMode={darkMode} />
                                <DetailItem icon={<CalendarOutlined />} label="Enrollment Date" value={enrollmentDate ? formatDate(enrollmentDate) : null} darkMode={darkMode} />
                                <DetailItem icon={<SafetyCertificateOutlined />} label="Admission Status" value={status} darkMode={darkMode} />
                                <DetailItem icon={<ClockCircleOutlined />} label="Semester Status" value={student?.semester_status} darkMode={darkMode} />
                                <DetailItem icon={<CalendarOutlined />} label="Last Updated" value={student?.updated_at ? formatDate(student.updated_at) : null} darkMode={darkMode} />
                            </DetailSection>
                        </div>
                    )}

                    {safeActivePage === "contact" && (
                        <div className="adsp-page-content">
                            <DetailSection
                                title="Contact Information"
                                subtitle="Student communication and location"
                                icon={<PhoneOutlined />}
                                darkMode={darkMode}
                            >
                                <DetailItem icon={<MailOutlined />} label="Email Address" value={email} darkMode={darkMode} />
                                <DetailItem icon={<PhoneOutlined />} label="Phone Number" value={phone} darkMode={darkMode} />
                                <DetailItem icon={<HomeOutlined />} label="Residential Address" value={address} darkMode={darkMode} />
                                <DetailItem
                                    icon={<EnvironmentOutlined />}
                                    label="City / Location"
                                    value={firstValue(student?.city, student?.town, student?.community, student?.county)}
                                    darkMode={darkMode}
                                />
                                <DetailItem icon={<MailOutlined />} label="Postal Address" value={student?.postal_address} darkMode={darkMode} />
                                <DetailItem
                                    icon={<PhoneOutlined />}
                                    label="Alternative Phone"
                                    value={firstValue(student?.alternative_phone, student?.alternate_phone, student?.other_phone, student?.emergency_contact)}
                                    darkMode={darkMode}
                                />
                            </DetailSection>
                        </div>
                    )}

                    {safeActivePage === "guardian" && (
                        <div className="adsp-page-content">
                            <DetailSection
                                title="Parent / Guardian"
                                subtitle="Family and emergency contact details"
                                icon={<ContactsOutlined />}
                                darkMode={darkMode}
                            >
                                <DetailItem icon={<UserOutlined />} label="Parent / Guardian Name" value={parentName} darkMode={darkMode} />
                                <DetailItem icon={<HeartOutlined />} label="Relationship" value={relationship} darkMode={darkMode} />
                                <DetailItem icon={<PhoneOutlined />} label="Contact Number" value={parentPhone} darkMode={darkMode} />
                                <DetailItem icon={<MailOutlined />} label="Email Address" value={parentEmail} darkMode={darkMode} />
                                <DetailItem icon={<UserOutlined />} label="Father's Name" value={student?.father_name} darkMode={darkMode} />
                                <DetailItem icon={<PhoneOutlined />} label="Father's Phone" value={student?.father_phone} darkMode={darkMode} />
                                <DetailItem icon={<UserOutlined />} label="Mother's Name" value={student?.mother_name} darkMode={darkMode} />
                                <DetailItem icon={<PhoneOutlined />} label="Mother's Phone" value={student?.mother_phone} darkMode={darkMode} />
                                <DetailItem
                                    icon={<PhoneOutlined />}
                                    label="Emergency Contact"
                                    value={firstValue(student?.emergency_contact, student?.emergency_contact_number, student?.next_of_kin_phone)}
                                    darkMode={darkMode}
                                />
                            </DetailSection>
                        </div>
                    )}

                    {safeActivePage === "additional" && (
                        <div className="adsp-page-content">
                            {extraDetails.length ? (
                                <DetailSection
                                    title="Additional Information"
                                    subtitle="Other available admission record fields"
                                    icon={<FileTextOutlined />}
                                    darkMode={darkMode}
                                >
                                    {extraDetails.map((item) => (
                                        <DetailItem
                                            key={item.key}
                                            icon={<FileTextOutlined />}
                                            label={item.label}
                                            value={item.value}
                                            darkMode={darkMode}
                                        />
                                    ))}
                                </DetailSection>
                            ) : (
                                <div className="adsp-empty">
                                    <Empty description="No additional information available." />
                                </div>
                            )}
                        </div>
                    )}
                </main>

                <footer className="adsp-footer">
                    <div className="adsp-footer-caption">
                        <span className="adsp-footer-mark">
                            <SafetyCertificateOutlined />
                        </span>
                        <div>
                            <strong>Student Admission Record</strong>
                            <span>Official school information</span>
                        </div>
                    </div>

                    <div className="adsp-footer-actions">
                        <Button
                            className="adsp-footer-close"
                            onClick={onClose}
                            icon={<CloseOutlined />}
                        >
                            Close
                        </Button>

                        <Button
                            type="primary"
                            className="adsp-footer-edit"
                            icon={<EditOutlined />}
                            onClick={() => onEdit(student)}
                        >
                            Edit Admission
                        </Button>
                    </div>
                </footer>
            </div>

            <style>{`
                /* =================================================
                   ENTIRE MODAL WINDOW — HEIGHT AND POSITION FIX
                   This changes the outside popup, not its contents.
                ================================================= */

                .adsp-modal-wrap {
                    position: fixed !important;
                    inset: 0 0 0 var(--adsp-sidebar-offset, 250px) !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    overflow: hidden !important;
                    padding: 50px 12px !important;
                    box-sizing: border-box !important;
                }

                .adsp-modal-wrap .adsp-modal,
                .adsp-modal-wrap .ant-modal {
                    top: auto !important;
                    margin: 0 auto !important;
                    padding-bottom: 0 !important;
                    max-width: 100% !important;
                    max-height: 100% !important;
                }

                .adsp-modal .ant-modal-content {
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    padding: 0 !important;
                    border: 1px solid #DDE5EC;
                    border-radius: 20px;
                    background: #F7F9FC;
                    box-shadow: 0 24px 80px rgba(2, 21, 39, .32);
                }

                .adsp-modal .ant-modal-body {
                    min-height: 0;
                    overflow: hidden;
                    padding: 0 !important;
                }

                .adsp-modal-wrap .ant-modal-mask {
                    background: rgba(3, 15, 28, .66);
                    backdrop-filter: blur(5px);
                }

                /* =================================================
                   ENTIRE PORTFOLIO HEIGHT
                   The whole popup is capped at 600px.
                   It leaves space above and below the popup.
                ================================================= */

                .adsp-portfolio {
                    --adsp-navy: #062A4B;
                    --adsp-blue: #063A6B;
                    --adsp-blue-light: #0F6DAB;
                    --adsp-gold: #F6C944;
                    --adsp-gold-deep: #C0750D;
                    --adsp-text: #182A3A;
                    --adsp-muted: #738091;
                    --adsp-border: #E2E8EF;
                    --adsp-surface: #FFFFFF;
                    --adsp-page-bg: #F5F8FB;

                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    height: min(600px, calc(100dvh - 100px));
                    max-height: calc(100dvh - 100px);
                    min-height: 0;
                    overflow: hidden;
                    color: var(--adsp-text);
                    background: var(--adsp-page-bg);
                    font-family: inherit;
                    box-sizing: border-box;
                }

                .adsp-portfolio *,
                .adsp-portfolio *::before,
                .adsp-portfolio *::after {
                    box-sizing: border-box;
                }

                .adsp-portfolio.adsp-dark {
                    --adsp-text: #EDF3F8;
                    --adsp-muted: #A5B3C1;
                    --adsp-border: #2B3B49;
                    --adsp-surface: #15212C;
                    --adsp-page-bg: #0E1720;
                }

                /* HEADER */

                .adsp-header {
                    position: relative;
                    flex: 0 0 auto;
                    overflow: hidden;
                    padding: 17px 23px 12px;
                    color: #FFFFFF;
                    background: linear-gradient(115deg, #062A4B 0%, #063A6B 62%, #0A5488 100%);
                }

                .adsp-header-decoration {
                    position: absolute;
                    z-index: 0;
                    pointer-events: none;
                    border-radius: 50%;
                }

                .adsp-decoration-one {
                    width: 245px;
                    height: 245px;
                    top: -170px;
                    right: -30px;
                    border: 30px solid rgba(246, 201, 68, .10);
                }

                .adsp-decoration-two {
                    width: 180px;
                    height: 180px;
                    bottom: -140px;
                    left: 47%;
                    background: rgba(255, 255, 255, .045);
                }

                .adsp-header-topline,
                .adsp-profile,
                .adsp-student-navigation {
                    position: relative;
                    z-index: 1;
                }

                .adsp-header-topline {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 14px;
                }

                .adsp-brand {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 0;
                }

                .adsp-brand-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 35px;
                    height: 35px;
                    flex: 0 0 35px;
                    border: 1px solid rgba(246, 201, 68, .38);
                    border-radius: 10px;
                    color: var(--adsp-gold);
                    background: rgba(255, 255, 255, .08);
                    font-size: 17px;
                }

                .adsp-brand-eyebrow {
                    display: block;
                    margin-bottom: 3px;
                    color: var(--adsp-gold);
                    font-size: 9px;
                    font-weight: 800;
                    letter-spacing: 1.3px;
                }

                .adsp-brand-title {
                    display: block;
                    color: #FFFFFF;
                    font-size: 15px;
                    line-height: 1.2;
                    font-weight: 800;
                }

                .adsp-close-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px;
                    height: 34px;
                    flex: 0 0 34px;
                    border: 1px solid rgba(255,255,255,.20);
                    border-radius: 10px;
                    color: #FFFFFF;
                    background: rgba(255,255,255,.09);
                    cursor: pointer;
                    transition: background .2s ease, transform .2s ease;
                }

                .adsp-close-button:hover {
                    transform: rotate(3deg);
                    color: #FFFFFF;
                    background: rgba(255,255,255,.19);
                }

                /* PROFILE */

                .adsp-profile {
                    display: grid;
                    grid-template-columns: 82px minmax(0, 1fr) 190px;
                    align-items: center;
                    gap: 17px;
                }

                .adsp-profile-photo {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    width: 82px;
                    height: 88px;
                    border: 3px solid rgba(246,201,68,.95);
                    border-radius: 15px;
                    color: #063A6B;
                    background: #E8F0F7;
                    box-shadow: 0 7px 19px rgba(0,0,0,.15);
                }

                .adsp-profile-fallback {
                    position: absolute;
                    font-size: 32px;
                }

                .adsp-profile-photo img {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .adsp-photo-badge {
                    position: absolute;
                    right: -1px;
                    bottom: -1px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 23px;
                    height: 23px;
                    border: 2px solid #063A6B;
                    border-radius: 50%;
                    color: #063A6B;
                    background: var(--adsp-gold);
                    font-size: 11px;
                }

                .adsp-profile-main {
                    min-width: 0;
                }

                .adsp-profile-kicker {
                    display: block;
                    margin-bottom: 4px;
                    color: rgba(255,255,255,.66);
                    font-size: 8px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                }

                .adsp-profile-main h2 {
                    overflow-wrap: anywhere;
                    margin: 0 0 8px !important;
                    color: #FFFFFF !important;
                    font-size: clamp(18px, 2vw, 24px) !important;
                    line-height: 1.12 !important;
                    font-weight: 800 !important;
                    letter-spacing: -.4px;
                }

                .adsp-profile-meta {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 6px 15px;
                    color: rgba(255,255,255,.78);
                    font-size: 10px;
                }

                .adsp-profile-meta span {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    min-width: 0;
                }

                .adsp-profile-meta .anticon {
                    color: var(--adsp-gold);
                }

                .adsp-profile-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    margin-top: 9px;
                }

                .adsp-status,
                .adsp-gender-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    margin: 0 !important;
                    padding: 3px 8px;
                    border: 1px solid transparent;
                    border-radius: 20px;
                    font-size: 9px;
                    line-height: 1.3;
                    font-weight: 800;
                }

                .adsp-status-active {
                    color: #D9F9CB;
                    border-color: rgba(144, 238, 144, .22);
                    background: rgba(55, 130, 65, .28);
                }

                .adsp-status-other {
                    color: #FFF0CC;
                    border-color: rgba(246,201,68,.23);
                    background: rgba(192,117,13,.23);
                }

                .adsp-status-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #88DB72;
                }

                .adsp-status-other .adsp-status-dot {
                    background: #F6C944;
                }

                .adsp-gender-tag {
                    color: #FFFFFF;
                    border-color: rgba(255,255,255,.18);
                    background: rgba(255,255,255,.08);
                }

                .adsp-profile-year {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    gap: 5px;
                    min-width: 0;
                    min-height: 75px;
                    padding: 12px 14px;
                    border: 1px solid rgba(255,255,255,.14);
                    border-radius: 12px;
                    background: rgba(0,0,0,.13);
                }

                .adsp-profile-year > span:first-child {
                    color: rgba(255,255,255,.61);
                    font-size: 8px;
                    font-weight: 800;
                    letter-spacing: 1px;
                }

                .adsp-profile-year strong {
                    overflow-wrap: anywhere;
                    color: #FFFFFF;
                    font-size: 14px;
                    line-height: 1.3;
                }

                .adsp-profile-year .adsp-year-caption {
                    color: rgba(255,255,255,.52);
                    font-size: 9px;
                }

                /* STUDENT NAVIGATION */

                .adsp-student-navigation {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    margin-top: 14px;
                    padding-top: 11px;
                    border-top: 1px solid rgba(255,255,255,.13);
                }

                .adsp-nav-student.ant-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    height: 29px;
                    padding: 0 10px;
                    border: 1px solid rgba(255,255,255,.19);
                    border-radius: 8px;
                    color: #FFFFFF;
                    background: rgba(255,255,255,.075);
                    box-shadow: none;
                    font-size: 10px;
                    font-weight: 700;
                }

                .adsp-nav-student.ant-btn:hover:not(:disabled) {
                    color: var(--adsp-gold) !important;
                    border-color: var(--adsp-gold) !important;
                    background: rgba(255,255,255,.11) !important;
                }

                .adsp-nav-student.ant-btn:disabled {
                    color: rgba(255,255,255,.35);
                    border-color: rgba(255,255,255,.08);
                    background: rgba(255,255,255,.025);
                }

                .adsp-navigation-count {
                    color: rgba(255,255,255,.66);
                    font-size: 10px;
                    font-weight: 700;
                    text-align: center;
                }

                /* TABS */

                .adsp-page-tabs {
                    display: flex;
                    flex: 0 0 auto;
                    align-items: stretch;
                    gap: 5px;
                    overflow-x: auto;
                    padding: 9px 20px 0;
                    border-bottom: 1px solid var(--adsp-border);
                    background: var(--adsp-surface);
                    scrollbar-width: thin;
                    scrollbar-color: #C8D2DD transparent;
                }

                .adsp-page-tab {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                    min-height: 38px;
                    flex: 0 0 auto;
                    padding: 0 13px 10px;
                    border: 0;
                    color: var(--adsp-muted);
                    background: transparent;
                    font-family: inherit;
                    font-size: 10.5px;
                    font-weight: 750;
                    white-space: nowrap;
                    cursor: pointer;
                    transition: color .2s ease;
                }

                .adsp-page-tab::after {
                    position: absolute;
                    right: 12px;
                    bottom: -1px;
                    left: 12px;
                    height: 3px;
                    border-radius: 4px 4px 0 0;
                    background: transparent;
                    content: "";
                }

                .adsp-page-tab:hover {
                    color: var(--adsp-blue-light);
                }

                .adsp-page-tab-active {
                    color: var(--adsp-blue);
                }

                .adsp-dark .adsp-page-tab-active {
                    color: var(--adsp-gold);
                }

                .adsp-page-tab-active::after {
                    background: var(--adsp-gold-deep);
                }

                .adsp-page-tab-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 13px;
                }

                /* SCROLLABLE DETAIL AREA */

                .adsp-body {
                    flex: 1 1 auto;
                    min-height: 0;
                    overflow: auto;
                    padding: 15px 20px;
                    overscroll-behavior: contain;
                    scrollbar-width: thin;
                    scrollbar-color: #BCC9D5 transparent;
                    background: var(--adsp-page-bg);
                }

                .adsp-body::-webkit-scrollbar {
                    width: 7px;
                    height: 7px;
                }

                .adsp-body::-webkit-scrollbar-thumb {
                    border-radius: 8px;
                    background: #BCC9D5;
                }

                .adsp-page-content {
                    display: flex;
                    flex-direction: column;
                    gap: 11px;
                    width: 100%;
                    min-width: 0;
                    animation: adspFadeIn .18s ease-out;
                }

                @keyframes adspFadeIn {
                    from {
                        opacity: .55;
                        transform: translateY(3px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* DETAIL SECTIONS */

                .adsp-detail-section {
                    overflow: hidden;
                    border: 1px solid var(--adsp-border);
                    border-radius: 13px;
                    background: var(--adsp-surface);
                    box-shadow: 0 3px 12px rgba(6,42,75,.035);
                }

                .adsp-section-heading {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 14px;
                    border-bottom: 1px solid var(--adsp-border);
                }

                .adsp-section-heading-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 33px;
                    height: 33px;
                    flex: 0 0 33px;
                    border-radius: 9px;
                    color: var(--adsp-blue);
                    background: #EDF4F9;
                    font-size: 15px;
                }

                .adsp-dark .adsp-section-heading-icon {
                    color: var(--adsp-gold);
                    background: #223341;
                }

                .adsp-section-heading-copy {
                    min-width: 0;
                }

                .adsp-section-heading h3 {
                    margin: 0;
                    color: var(--adsp-text);
                    font-size: 12px;
                    line-height: 1.35;
                    font-weight: 800;
                }

                .adsp-section-heading p {
                    margin: 3px 0 0;
                    color: var(--adsp-muted);
                    font-size: 9.5px;
                    line-height: 1.35;
                }

                .adsp-detail-grid {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 0;
                    padding: 3px 12px;
                }

                .adsp-detail-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 9px;
                    min-width: 0;
                    padding: 12px 8px;
                    border-bottom: 1px solid rgba(130,150,170,.13);
                }

                .adsp-detail-item:nth-last-child(-n + 3) {
                    border-bottom-color: transparent;
                }

                .adsp-detail-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    flex: 0 0 28px;
                    border: 1px solid var(--adsp-border);
                    border-radius: 8px;
                    color: var(--adsp-blue);
                    background: var(--adsp-page-bg);
                    font-size: 12px;
                }

                .adsp-dark .adsp-detail-icon {
                    color: var(--adsp-gold);
                }

                .adsp-detail-copy {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                }

                .adsp-detail-label {
                    color: var(--adsp-muted);
                    font-size: 9px;
                    line-height: 1.3;
                    font-weight: 700;
                }

                .adsp-detail-value {
                    overflow-wrap: anywhere;
                    color: var(--adsp-text);
                    font-size: 11px;
                    line-height: 1.45;
                    font-weight: 700;
                }

                /* NOTE */

                .adsp-information-note {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 12px;
                    border: 1px solid #E7D9A8;
                    border-radius: 10px;
                    color: #75602D;
                    background: #FFF9E9;
                    font-size: 10px;
                    line-height: 1.5;
                }

                .adsp-dark .adsp-information-note {
                    border-color: #54492D;
                    color: #E6D6A5;
                    background: #2B271D;
                }

                .adsp-note-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    flex: 0 0 28px;
                    border-radius: 8px;
                    color: #A97811;
                    background: rgba(246,201,68,.20);
                    font-size: 13px;
                }

                .adsp-empty {
                    padding: 25px 10px;
                    border: 1px solid var(--adsp-border);
                    border-radius: 12px;
                    background: var(--adsp-surface);
                }

                /* FOOTER */

                .adsp-footer {
                    display: flex;
                    flex: 0 0 auto;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 11px 20px;
                    border-top: 1px solid var(--adsp-border);
                    background: var(--adsp-surface);
                }

                .adsp-footer-caption {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    min-width: 0;
                }

                .adsp-footer-mark {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    flex: 0 0 32px;
                    border-radius: 9px;
                    color: var(--adsp-blue);
                    background: #EDF4F9;
                    font-size: 15px;
                }

                .adsp-dark .adsp-footer-mark {
                    color: var(--adsp-gold);
                    background: #223341;
                }

                .adsp-footer-caption strong,
                .adsp-footer-caption span {
                    display: block;
                }

                .adsp-footer-caption strong {
                    color: var(--adsp-text);
                    font-size: 10px;
                    line-height: 1.35;
                }

                .adsp-footer-caption div > span {
                    margin-top: 2px;
                    color: var(--adsp-muted);
                    font-size: 9px;
                }

                .adsp-footer-actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 8px;
                    flex: 0 0 auto;
                }

                .adsp-footer-actions .ant-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    height: 35px;
                    padding: 0 13px;
                    border-radius: 9px;
                    font-size: 10.5px;
                    font-weight: 750;
                }

                .adsp-footer-close {
                    color: var(--adsp-muted);
                    border-color: var(--adsp-border);
                    background: var(--adsp-surface);
                }

                .adsp-footer-close:hover {
                    color: var(--adsp-blue) !important;
                    border-color: var(--adsp-blue) !important;
                }

                .adsp-footer-edit {
                    border-color: var(--adsp-gold-deep) !important;
                    color: #FFFFFF !important;
                    background: var(--adsp-gold-deep) !important;
                    box-shadow: 0 3px 9px rgba(192,117,13,.16);
                }

                .adsp-footer-edit:hover {
                    border-color: #A96509 !important;
                    background: #A96509 !important;
                }

                /* TABLET */

                @media (max-width: 1050px) {
                    .adsp-profile {
                        grid-template-columns: 72px minmax(0, 1fr) 155px;
                        gap: 12px;
                    }

                    .adsp-profile-photo {
                        width: 72px;
                        height: 78px;
                    }

                    .adsp-detail-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }

                    .adsp-detail-item:nth-last-child(-n + 3) {
                        border-bottom-color: rgba(130,150,170,.13);
                    }

                    .adsp-detail-item:nth-last-child(-n + 2) {
                        border-bottom-color: transparent;
                    }
                }

                /* SMALLER SCREENS
                   More space above and below the entire popup.
                */

                @media (max-width: 760px) {
                    .adsp-modal-wrap {
                        inset: 0 !important;
                        padding: 40px 8px !important;
                    }

                    .adsp-modal-wrap .adsp-modal,
                    .adsp-modal-wrap .ant-modal {
                        width: 100% !important;
                        max-width: 100% !important;
                        max-height: 100% !important;
                    }

                    .adsp-portfolio {
                        height: min(600px, calc(100dvh - 80px));
                        max-height: calc(100dvh - 80px);
                    }

                    .adsp-header {
                        padding: 13px 15px 10px;
                    }

                    .adsp-profile {
                        grid-template-columns: 65px minmax(0, 1fr);
                        gap: 12px;
                    }

                    .adsp-profile-photo {
                        width: 65px;
                        height: 70px;
                    }

                    .adsp-profile-year {
                        grid-column: 2;
                        min-height: auto;
                        padding: 8px 10px;
                    }

                    .adsp-profile-year .adsp-year-caption {
                        display: none;
                    }

                    .adsp-profile-main h2 {
                        font-size: 18px !important;
                    }

                    .adsp-profile-meta {
                        gap: 5px 10px;
                        font-size: 9px;
                    }

                    .adsp-profile-year strong {
                        font-size: 12px;
                    }

                    .adsp-page-tabs {
                        padding-right: 10px;
                        padding-left: 10px;
                    }

                    .adsp-page-tab {
                        padding-right: 10px;
                        padding-left: 10px;
                    }

                    .adsp-body {
                        padding: 11px;
                    }

                    .adsp-footer {
                        padding: 9px 11px;
                    }
                }

                /* MOBILE */

                @media (max-width: 480px) {
                    .adsp-modal .ant-modal-content {
                        border-radius: 14px;
                    }

                    .adsp-header-topline {
                        margin-bottom: 10px;
                    }

                    .adsp-brand-icon {
                        width: 31px;
                        height: 31px;
                        flex-basis: 31px;
                    }

                    .adsp-brand-title {
                        font-size: 13px;
                    }

                    .adsp-brand-eyebrow {
                        font-size: 8px;
                    }

                    .adsp-profile {
                        grid-template-columns: 57px minmax(0, 1fr);
                        gap: 10px;
                    }

                    .adsp-profile-photo {
                        width: 57px;
                        height: 63px;
                        border-radius: 11px;
                    }

                    .adsp-profile-fallback {
                        font-size: 25px;
                    }

                    .adsp-profile-main h2 {
                        margin-bottom: 5px !important;
                        font-size: 16px !important;
                    }

                    .adsp-profile-kicker {
                        font-size: 7px;
                    }

                    .adsp-profile-meta {
                        font-size: 8.5px;
                    }

                    .adsp-profile-tags {
                        margin-top: 6px;
                    }

                    .adsp-status,
                    .adsp-gender-tag {
                        padding: 2px 6px;
                        font-size: 8px;
                    }

                    .adsp-profile-year {
                        grid-column: 1 / -1;
                        padding: 7px 9px;
                    }

                    .adsp-profile-year > span:first-child {
                        font-size: 7px;
                    }

                    .adsp-profile-year strong {
                        font-size: 11px;
                    }

                    .adsp-student-navigation {
                        gap: 5px;
                        margin-top: 10px;
                        padding-top: 9px;
                    }

                    .adsp-nav-student.ant-btn {
                        height: 28px;
                        padding: 0 7px;
                        font-size: 9px;
                    }

                    .adsp-navigation-count {
                        font-size: 8px;
                    }

                    .adsp-page-tabs {
                        gap: 0;
                        padding: 8px 6px 0;
                    }

                    .adsp-page-tab {
                        gap: 5px;
                        min-height: 36px;
                        padding: 0 8px 9px;
                        font-size: 9px;
                    }

                    .adsp-page-tab-icon {
                        font-size: 11px;
                    }

                    .adsp-body {
                        padding: 8px;
                    }

                    .adsp-section-heading {
                        padding: 10px;
                    }

                    .adsp-section-heading-icon {
                        width: 29px;
                        height: 29px;
                        flex-basis: 29px;
                    }

                    .adsp-detail-grid {
                        grid-template-columns: minmax(0, 1fr);
                        padding: 2px 10px;
                    }

                    .adsp-detail-item,
                    .adsp-detail-item:nth-last-child(-n + 2),
                    .adsp-detail-item:nth-last-child(-n + 3) {
                        padding: 9px 2px;
                        border-bottom-color: rgba(130,150,170,.13);
                    }

                    .adsp-detail-item:last-child {
                        border-bottom-color: transparent;
                    }

                    .adsp-detail-value {
                        font-size: 10.5px;
                    }

                    .adsp-information-note {
                        align-items: flex-start;
                        font-size: 9px;
                    }

                    .adsp-footer {
                        align-items: flex-start;
                        gap: 8px;
                        padding: 8px;
                    }

                    .adsp-footer-caption {
                        gap: 6px;
                    }

                    .adsp-footer-mark {
                        width: 27px;
                        height: 27px;
                        flex-basis: 27px;
                    }

                    .adsp-footer-caption strong {
                        font-size: 8.5px;
                    }

                    .adsp-footer-caption div > span {
                        font-size: 8px;
                    }

                    .adsp-footer-actions {
                        gap: 5px;
                    }

                    .adsp-footer-actions .ant-btn {
                        height: 32px;
                        padding: 0 8px;
                        font-size: 9px;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .adsp-page-content,
                    .adsp-close-button,
                    .adsp-page-tab {
                        animation: none !important;
                        transition: none !important;
                    }
                }
            `}</style>
        </Modal>
    );
};

export default AdmissionStudentDetailsPopUp;